import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, EventEmitter, OnInit, Output } from '@angular/core';
import {
  NavigationEnd,
  Router,
  RouterLink,
  RouterModule,
} from '@angular/router';
import * as feather from 'feather-icons';
import { filter, distinctUntilChanged } from 'rxjs';
import SimpleBar from 'simplebar';
import { routeUrls } from '../../constants/constants';
import { UserService } from '../../services/user.service';
import { AuthService } from '@auth0/auth0-angular';
import { LoadingService } from '../../services/loading.service';
import { TournamentService } from '../../services/tournament.service';
import { FeatureFlagService } from '../../services/feature-flag.service';
import { CommonService } from '../../services/common.service';
import { ClubService } from '../../services/club.service';
import { CricteamsChatService } from '../../services/cricteams-chat.service';
import { ClubSubscriptionInfo } from '../../models/club/club-info.model';
import { environment } from '../../../environments/environment';


@Component({
  selector: 'cricteams-sidebar',
  standalone: true,
  imports: [CommonModule, RouterLink, RouterModule],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.scss'],
})
export class SidebarComponent implements AfterViewInit, OnInit {
  constants = routeUrls;
  // Help Center now lives on the standalone Astro site (help.crickteams.com /
  // help-qa.crickteams.com); the sidebar link opens it in a new tab.
  helpCenterUrl = environment.helpCenterUrl;
  isManagerOrCaptain!: boolean;
  @Output() sidebarToggle = new EventEmitter<void>();
  public isShowTournamentButton = false;
  public isManager = false;
  // CRT-471: club fee config + expense config are treasurer-only (backend gates match).
  // Drives visibility of the Settings → Fees / Expenses submenu items below.
  public isTreasurer = false;
  public isExpenseEnabled = false;
  public isExpenseEnabledGlobally = false;
  public settingsOpen = false;
  public financesOpen = false;
  public hasFeatureFlags = false;
  public isChatEnabled = false;
  // CRT-858: Bind the badge through `unreadCount$ | async` so the DOM repaints when
  // Stream's WebSocket events fire outside Angular's NgZone (which is the default for
  // the Stream JS client). A plain `.subscribe(v => this.chatUnreadCount = v)` assigns
  // the field but never triggers change detection on non-chat routes, so the sidebar
  // badge appeared stuck at 0 while the user was anywhere other than /chat.
  public chatUnreadCount$ = this.cricteamsChatService.unreadCount$;
  public clubSubscription: ClubSubscriptionInfo | null = null;

  constructor(private el: ElementRef, private router: Router, private userService:UserService,private authService: AuthService, private loadingService: LoadingService, private tournamentService: TournamentService, private featureFlagService: FeatureFlagService, private commonService: CommonService, private clubService: ClubService, private cricteamsChatService: CricteamsChatService) {
    this.userService.isManagerOrCaptain.subscribe((isManagerOrCaptain) => {
      this.isManagerOrCaptain = isManagerOrCaptain;
    });

    // Load feature flags when the active club becomes available (first login, refresh, or club switch)
    this.userService.currentClubId$
      .pipe(filter(id => !!id), distinctUntilChanged())
      .subscribe((clubId) => {
        this.loadClubFeatureFlags(clubId);
        this.loadClubSubscription(clubId);
        // CRT-471 Task 8.3.3: resolve Fees/Expenses visibility from club capabilities.
        // Falls back to the legacy isTreasurer role check when capabilities are absent.
        this.userService.currentClubCapabilities().then((caps) => {
          if (caps) {
            this.isTreasurer = caps.canManageFees === true || caps.canManageExpenseConfig === true;
          } else {
            this.userService.isTreasurerOfCurrentClub().then((t) => (this.isTreasurer = t));
          }
        });
      });

    // Refresh subscription data when club info is updated (e.g. after plan upgrade)
    this.clubService.isClubUpdated
      .pipe(filter(v => v === true))
      .subscribe(() => {
        const clubId = localStorage.getItem('currentClubId');
        if (clubId) {
          this.loadClubSubscription(clubId);
        }
      });
  }

  ngAfterViewInit() {
    const element = this.el.nativeElement.querySelector('[simplebar]');
    if (element) {
      new SimpleBar(element);
    }
    feather.replace();
  }

  activeManu = '';
  manuOpen = '';

  ngOnInit(): void {
    this.activeManu = this.router.url;
    window.scrollTo(0, 0);
    this.manuOpen = this.activeManu;
    this.settingsOpen = this.activeManu.includes('/' + this.constants.SETTINGS);
    this.financesOpen = this.isFinancesActive();
    this.openMenu();
    this.userService.isUserManagerOrCaptain();
    this.commonService.isManager().then((isManager) => {
      this.isManager = isManager;
    });
    this.tournamentService.isShowTournamentButton.subscribe((response) => {
      this.isShowTournamentButton = response;
      });
  }

  private async loadClubFeatureFlags(clubId: string): Promise<void> {
    try {
      const flags = await this.featureFlagService.getClubFeatureFlags(clubId);
      if (flags) {
        const expenseFlag = flags.find(f => f.key === 'expense-tracking');
        this.isExpenseEnabled = expenseFlag?.isEnabledForClub ?? false;
        this.isExpenseEnabledGlobally = expenseFlag?.isEnabledGlobally ?? false;

        const chatFlag = flags.find(f => f.key === 'club-chat');
        this.isChatEnabled = chatFlag?.isEnabledForClub ?? chatFlag?.isEnabledGlobally ?? false;

        // Initialize chat when enabled. The badge binds to `chatUnreadCount$ | async`
        // directly (see field declaration), so no manual subscription is needed here.
        if (this.isChatEnabled) {
          this.cricteamsChatService.initForClub(clubId);
        }

        this.hasFeatureFlags = flags.length > 0;
      } else {
        this.isExpenseEnabled = false;
        this.isExpenseEnabledGlobally = false;
        this.isChatEnabled = false;
        this.hasFeatureFlags = false;
      }
    } catch (error) {
      console.error('Error loading club feature flags:', error);
    }
  }

  private loadClubSubscription(clubId: string): void {
    try {
      const userInfo = localStorage.getItem('userBaseInfo');
      if (userInfo) {
        const data = JSON.parse(userInfo);
        const club = data.clubs?.find((c: { id: string }) => c.id === clubId);
        this.clubSubscription = club?.subscription ?? null;
      }
    } catch {
      this.clubSubscription = null;
    }
  }

  get planBadgeLabel(): string {
    if (!this.clubSubscription?.planDisplayName) return '';
    // planDisplayName is like "Cricket Club Free" — extract the tier name
    const parts = this.clubSubscription.planDisplayName.split(' ');
    return parts[parts.length - 1] ?? '';
  }

  get isProOrAbove(): boolean {
    const planId = this.clubSubscription?.planId ?? '';
    return planId.includes('pro') || planId.includes('premium');
  }

  get planBadgeClass(): string {
    const planId = this.clubSubscription?.planId ?? '';
    if (planId.includes('premium')) return 'bg-amber-100 text-amber-700 border-amber-200';
    if (planId.includes('pro')) return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    return 'bg-gray-100 text-gray-600 border-gray-200';
  }

  isActive(route: string): boolean {
    return this.activeManu.includes(route);
  }

  openMenu() {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.activeManu = this.router.url;
        window.scrollTo(0, 0);
        this.manuOpen = this.activeManu;
        if (this.activeManu.includes('/' + this.constants.SETTINGS)) {
          this.settingsOpen = true;
        }
        if (this.isFinancesActive()) {
          this.financesOpen = true;
        }
      });
  }

  toggleSubmenu(item: string): void {
    if (item === 'settings') {
      this.settingsOpen = !this.settingsOpen;
      return;
    }
    if (item === 'finances') {
      this.financesOpen = !this.financesOpen;
      return;
    }
    if (this.manuOpen === item) {
      this.manuOpen = '';
    } else {
      this.manuOpen = item;
    }
  }

  isFinancesActive(): boolean {
    return (
      this.activeManu.includes('/' + this.constants.EXPENSES) ||
      this.activeManu.includes('/' + this.constants.FEE_PAYMENTS)
    );
  }

  async toggleSideBar() {
    this.authService.getAccessTokenSilently().subscribe((token) => {
    const decodedToken = this.decodeToken(token);

    if (!decodedToken || !decodedToken.exp) {
      this.router.navigate(['/home']);
      return;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (decodedToken.exp < currentTime) {
      this.router.navigate(['/home']);
      return;
    }
    });

    if (window.innerWidth <= 1000) {
      this.sidebarToggle.emit();
    }
  }

  decodeToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      return null;
    }
  }
}
